import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, ElementRef, HostListener } from '@angular/core';
import { CloudNotificationService } from 'src/app/services/cloud-notification.service';
import { Observable, Subject } from 'rxjs';
import { faTimes, faCheck, faRedo, faBell, faSpinner, faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import { GetNotifications } from 'src/app/interfaces/services/notification.interface';
import { CloudNotificationResponse } from 'src/app/models/cloud-notification-response.model';
import { CloudRefreshService } from 'src/app/services/cloud-refresh.service';
import { RefreshIdentifier } from 'src/app/enums/refresh-identifier.enum';
import { CloudProfileService } from 'src/app/services/cloud-profile.service';
import { User } from 'src/app/models/user.model';
import { NcacheSubscription } from 'src/app/models/ncache-subscription.model';
import { Refresh } from 'src/app/interfaces/services/notification.interface';
import { CloudNotificationStyle } from 'src/app/models/cloud-notification-style.model';
import { CloudAuthenticationService } from 'src/app/services/cloud-authentication.service';
import { LogoutEvent } from 'src/app/enums/logout-event.enum';

@Component({
  selector: 'app-notification-panel',
  templateUrl: './notification-panel.component.html',
  styleUrls: ['./notification-panel.component.sass'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class NotificationPanelComponent implements OnInit, GetNotifications, Refresh {

  faTimes = faTimes;
  faCheck = faCheck;
  faRedo = faRedo;
  faBell = faBell;
  faSpinner = faSpinner;
  faCheckCircle = faCheckCircle;

  toastsIds: string[] = [];
  panelDisplayStatus: Observable<boolean>;
  cloudUser: User;
  cloudSubscription: NcacheSubscription;
  notifications: CloudNotificationResponse[] = [];
  isPanelOpen = false;

  serverClientTimeDiff: number = null;

  constructor(private cdRef: ChangeDetectorRef,
    private profileSvc: CloudProfileService,
    public notificationSvc: CloudNotificationService,
    private refreshService: CloudRefreshService,
    private authSvc: CloudAuthenticationService) {

      // REMOVE NOTIFICATIONS UPON EVENT
      this.authSvc.logoutEvent.subscribe(res => {
        switch (res) {
          case LogoutEvent.ClearNotifications:
            this.clearLocalNotifications();
            break;

          default:
            break;
        }
      });

      // ADD NOTIFICATION EVENTS WHEN LOGGING IN
      this.authSvc.loginEvent.subscribe(() => {
        this.registerNotifications();
      });

    }

  closePanel(event: Event): void {
    this.notificationSvc.panelDisplayStatus.next(false)
  }

  register<T>(identifier: RefreshIdentifier, params: any, refreshInterval?: number): Subject<T> {
    return this.refreshService.register<any>(identifier, params, refreshInterval);
  }

  unregister(identifier: RefreshIdentifier) {
    this.refreshService.unregister(identifier);
  }

  ngOnInit(): void {
    this.panelDisplayStatus = this.notificationSvc.panelDisplayStatus.asObservable();
    this.panelDisplayStatus.subscribe(x => {
      this.isPanelOpen = x;

      if (this.isPanelOpen && this.notificationSvc.notificationCount > 0) {
        this.notificationSvc.readNotification(this.notifications).subscribe();
        this.notifications = this.notifications.map(notif => {
          notif.isRead = true;
          return notif;
        });
      }
      this.notificationSvc.notificationCountSubject.next(this.notifications.filter(notif => !notif.isRead).length);
      this.cdRef.detectChanges();
    });

    this.registerNotifications();

    this.profileSvc.getUser().subscribe(user => {
      this.cloudUser = user;
    });
  }

  registerNotifications() {
    this.register<any>(RefreshIdentifier.notification, {}, 5)
      .subscribe(cloudNotifications => {
        this.notifications = cloudNotifications;
        this.errorCorrectTimeOnNotifications(this.notifications);
        this.notificationSvc.notificationCountSubject.next(this.notifications.filter(notif => !notif.isRead).length);
        this.generateToastNotications();
        this.cdRef.detectChanges();
      });
  }

  errorCorrectTimeOnNotifications(notifications: CloudNotificationResponse[]): void {
    notifications.forEach(notification => {
      let date = new Date();
      let offsetTime = date.getTime();
      notification.updateTime = this.toIsoString(new Date(offsetTime - (notification.timeDiff * 1000)));
    })
  }

  toIsoString(date: Date) {
    var tzo = -date.getTimezoneOffset(),
      dif = tzo >= 0 ? '+' : '-',
      pad = function(num: number) {
          return (num < 10 ? '0' : '') + num;
      };

    return date.getFullYear() +
        '-' + pad(date.getMonth() + 1) +
        '-' + pad(date.getDate()) +
        'T' + pad(date.getHours()) +
        ':' + pad(date.getMinutes()) +
        ':' + pad(date.getSeconds()) +
        dif + pad(Math.floor(Math.abs(tzo) / 60)) +
        ':' + pad(Math.abs(tzo) % 60);
  }

  refresh() {
    this.getNotifications().subscribe(cloudNotifications => {
      this.notifications = cloudNotifications;
    });
  }

  dismissAll() {
    this.notificationSvc.dismissAll(this.notifications).subscribe();
    this.notifications.length = 0;
  }

  // implementations
  getNotifications(): Observable<CloudNotificationResponse[]> {
    return this.notificationSvc.getNotifications();
  }

  generateToastNotications() {
    this.notifications.forEach(notification => {
      if (!notification.isToastRead && !this.toastsIds.includes(notification.id.toString())) {

        switch (notification.status) {

          case "completed":
            this.notificationSvc.createorUpdateToastNotification(
              notification.id.toString(),
              notification.jobTitle,
              CloudNotificationStyle.success
            );
            this.toastsIds.push(notification.id.toString());
            break;

          case "failed":
            this.notificationSvc.createorUpdateToastNotification(
              notification.id.toString(),
              notification.jobTitle,
              CloudNotificationStyle.danger
            );
            this.toastsIds.push(notification.id.toString());
            break;

          default:
            break;
        }

      }
    });
  }

  clearLocalNotifications() {
    delete this.notifications;
    this.notifications = [];
  }
}
